import React, { useEffect, useState, useCallback } from "react";
import styles from "./AddProduct.module.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaSearch, FaEdit, FaTrash } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import WebService from "../../Services/WebService";
import { mediaUrl } from "../../Services/mediaUrl";
import AddProductPanel from "./AddProductPanel";
import { navigateToBlade, usePathBlade } from "../../Customs/FullWidthBlade/FullWidthBlade";
import DocUploadPopup from "./DocUploadPopup";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "../../Services/Store";

// ---------- Types ----------
interface ProductImage {
  document_id: number;
  file_name: string;
}
interface Product {
  id: number;
  name: string;
  sku_id: string;
  quantity: number;
  basic_door_price: number;
  double_door_price: number;
  hsn_code?: string | number | null;
  images: ProductImage[];
}
interface Pagination {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
}
interface Category {
  category_id: number;
  category_name: string;
}
interface InventoryAccess {
  write?: boolean;
  update?: boolean;
  delete?: boolean;
  [key: string]: any;
}

const DEFAULT_LIMIT = 10;

const AddProduct: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const bladeOpen = usePathBlade("/inventory/product-design/:id");

  const displayHSN = (hsn?: string | number | null) =>
    hsn && String(hsn).trim() && String(hsn).trim().toLowerCase() !== "null"
      ? String(hsn)
      : "-";

  // --- Derived filters from URL ---
  const search = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "";
  const sortOrder = searchParams.get("sort_order") || "";
  const categoryFilter = searchParams.get("category") || "";
  const currentPage = Number(searchParams.get("page")) || 1; // kept for backward-compat; not used by infinite scroll

  // --- Local state ---
  const [searchInput, setSearchInput] = useState(search);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [products, setProducts] = useState<Product[]>([]);
  const [, setPagination] = useState<Pagination | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [showDocPopup, setShowDocPopup] = useState(false);
  const [newProductId, setNewProductId] = useState<number | null>(null);

  // ---------- RBAC: Inventory Permissions ----------
  const accessList = useSelector((state: RootState) => state.auth.access || []);
  const inventoryAccess: InventoryAccess =
    accessList.find((item: any) => item.code === "inventory_management") || {};
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isAdmin =
    String(currentUser?.user_type ?? currentUser?.role_name ?? "").toUpperCase() === "ADMIN";

  // Trending is recomputed monthly by the backend; admins can force it now.
  const [refreshingTrending, setRefreshingTrending] = useState(false);
  const handleRefreshTrending = async () => {
    setRefreshingTrending(true);
    try {
      const res: any = await WebService.postAPI({
        action: `/v1/door-management/dashboard/trending/refresh`,
        body: {},
      });
      const data = res?.data || {};
      const count = Array.isArray(data.products) ? data.products.length : 0;
      if (data.updated) {
        toast.success(`Trending updated: ${count} best-selling product(s) from the last ${data.lookback_days} days.`);
      } else {
        toast.info(`No sales in the last ${data.lookback_days ?? 30} days. Trending list kept as is.`);
      }
    } catch {
      // WebService shows the error toast
    } finally {
      setRefreshingTrending(false);
    }
  };

  // --- Infinite scroll state ---
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isFetchingNext, setIsFetchingNext] = useState<boolean>(false);

  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  // Build a key that changes whenever filters/search change (not when page increments)
  const queryKey = JSON.stringify({
    search,
    statusFilter,
    sortOrder,
    categoryFilter,
    limit: DEFAULT_LIMIT,
  });

  // --- Debounced search input (for smoother UX) ---
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // --- Keep debounced search in sync with URL ---
  useEffect(() => {
    if (debouncedSearch !== search) {
      const params = new URLSearchParams(searchParams.toString());
      if (debouncedSearch) params.set("search", debouncedSearch);
      else params.delete("search");
      // We used to reset page in the URL; harmless to keep, but not required anymore
      params.set("page", "1");
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, [debouncedSearch]);

  // --- Keep filter controls in sync with URL on page mount/URL change
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // --- Fetch Categories ---
  useEffect(() => {
    WebService.getAPI({
      action: "/v1/door-management/product/categories?type=NO_SUB_CATEGORY",
    }).then((res: any) => {
      setCategories(Array.isArray(res?.data) ? res.data : []);
    });
  }, []);

  // --- Core fetcher that returns a page (does not mutate state directly) ---
  const fetchPage = useCallback(
    async (pageNumber: number) => {
      const query: Record<string, string> = {
        limit: String(DEFAULT_LIMIT),
        page_number: String(pageNumber),
      };
      if (search) query["product_like"] = search;
      if (statusFilter) query["status"] = statusFilter;
      if (sortOrder) query["sort_order"] = sortOrder;
      if (categoryFilter) query["category_id"] = categoryFilter;

      const queryString = Object.entries(query)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");

      const res: any = await WebService.getAPI({
        action: `/v1/door-management/product?${queryString}`,
      });
      const list: Product[] = Array.isArray(res?.data?.products)
        ? res.data.products
        : [];
      const pg: Pagination | null = res?.data?.pagination || null;

      return { list, pg };
    },
    [search, statusFilter, sortOrder, categoryFilter]
  );

  // --- Initial / Reset load whenever filters/search change ---
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setIsFetchingNext(false);
      setHasMore(true);
      setPage(1);

      try {
        const { list, pg } = await fetchPage(1);
        if (cancelled) return;
        setProducts(list);
        setPagination(pg);

        // Determine if more pages exist
        const more = pg ? pg.current_page < pg.total_pages : list.length === DEFAULT_LIMIT;
        setHasMore(more);
      } catch {
        if (cancelled) return;
        setProducts([]);
        setPagination(null);
        setHasMore(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [queryKey, fetchPage]);

  // --- Fetch next page (append) for infinite scroll ---
  const fetchNextPage = useCallback(async () => {
    if (isFetchingNext || !hasMore) return;
    setIsFetchingNext(true);
    const nextPage = page + 1;

    try {
      const { list, pg } = await fetchPage(nextPage);
      setProducts((prev) => [...prev, ...list]);
      setPagination(pg);
      setPage(nextPage);

      const more = pg ? pg.current_page < pg.total_pages : list.length === DEFAULT_LIMIT;
      setHasMore(more);
    } catch {
      // Silent fail; user can trigger again by scrolling
    } finally {
      setIsFetchingNext(false);
    }
  }, [page, hasMore, isFetchingNext, fetchPage]);

  // --- Observe sentinel to trigger next page load ---
  useEffect(() => {
    if (!sentinelRef.current || bladeOpen) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !loading) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "300px 0px", threshold: 0 }
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [fetchNextPage, loading, bladeOpen]);

  // --------- Delete Product with Confirmation ---------
  const deleteProduct = async (id: number) => {
    const confirm = await Swal.fire({
      title: "Delete Product",
      text: "Are you sure you want to delete this product?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      heightAuto: false,
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;
    try {
      await WebService.deleteAPI({
        action: `/v1/door-management/product/${id}`,
      });
      // Instead of refetching everything from scratch, refresh from first page to keep list accurate
      // (kept your original behavior)
      // fetchProducts(); // old
      // New reset load for page 1 with current filters:
      setPage(1);
      setHasMore(true);
      setIsFetchingNext(false);
      setLoading(true);
      const { list, pg } = await fetchPage(1);
      setProducts(list);
      setPagination(pg);
      const more = pg ? pg.current_page < pg.total_pages : list.length === DEFAULT_LIMIT;
      setHasMore(more);
      setLoading(false);
    } catch {
      // toast.error("Failed to delete product.");
    }
  };

  // --- Handler helpers (all update URL only) ---
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // debouncedSearch will update URL
  };
  const handleCategoryChange = (val: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (val) params.set("category", val);
    else params.delete("category");
    params.set("page", "1"); // harmless now
    setSearchParams(params);
  };
  const handleStatusChange = (val: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (val) params.set("status", val);
    else params.delete("status");
    params.set("page", "1");
    setSearchParams(params);
  };
  const handleSortChange = (val: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (val) params.set("sort_order", val);
    else params.delete("sort_order");
    params.set("page", "1");
    setSearchParams(params);
  };
  // --- UI helpers ---
  const getStockLabel = (qty: number) => {
    if (qty === 0) return <span className={styles.outStock}>Out of Stock</span>;
    if (qty <= 5) return <span className={styles.lowStock}>Low Stock</span>;
    return <span className={styles.inStock}>In Stock</span>;
  };

  // --- Row click handler (navigate to detail page) ---
  const handleRowClick = (product: Product) => {
    navigateToBlade(
      navigate,
      `/inventory/product-design/${product.id}`,
      searchParams,
      { product }
    );
  };

  // --- Render ---
  return (
    <div className={styles.wrapper}>
      <div inert={bladeOpen ? true : undefined} aria-hidden={bladeOpen || undefined}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 7,
          marginBottom: 8,
        }}
      >
        <span className={styles.pageTitle}>Product</span>
        <div className={styles.buttonRow}>
          <button className={`${styles.bigBtn} ${styles.active}`}>
            Product Inventory
          </button>
          <button
            className={styles.bigBtn}
            onClick={() => navigate("/inventory/product-categories")}
          >
            Product Categories
          </button>
          <button
            className={styles.bigBtn}
            onClick={() => navigate("/inventory/wood-qualities")}
          >
            Wood Qualities
          </button>
        </div>
      </div>

      <div className={styles.toolbar}>
        <form className={styles.searchForm} onSubmit={handleSearchSubmit}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search products…"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              // debounced search will update URL
              if (currentPage !== 1) {
                const params = new URLSearchParams(searchParams.toString());
                params.set("page", "1");
                setSearchParams(params, { replace: true });
              }
            }}
            autoComplete="off"
          />
        </form>
        <select
          className={styles.dropdown}
          style={{ minWidth: 180, marginLeft: 12, marginRight: 10 }}
          value={categoryFilter}
          onChange={(e) => handleCategoryChange(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat.category_id} value={cat.category_id}>
              {cat.category_name}
            </option>
          ))}
        </select>
        <div className={styles.filterButtons}>
          <select
            className={styles.dropdown}
            style={{ minWidth: 140 }}
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            <option value="">Filter by Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="OUT_OF_STOCK">Out of Stock</option>
          </select>
          <select
            className={styles.dropdown}
            value={sortOrder}
            onChange={(e) => handleSortChange(e.target.value)}
          >
            <option value="">Sort by Date</option>
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>
        {isAdmin && (
          <button
            className={styles.addBtn}
            onClick={handleRefreshTrending}
            disabled={refreshingTrending}
            title="Recompute trending products from the last 30 days of sales (runs automatically every month)"
            style={{ marginRight: 10, background: "#fff", border: "1px solid #FFD400" }}
          >
            {refreshingTrending ? "Refreshing..." : "Refresh Trending Now"}
          </button>
        )}
        <button
          className={styles.addBtn}
          onClick={() => {
            setEditProduct(null);
            setShowPanel(true);
          }}
          disabled={!inventoryAccess?.write}
          style={
            !inventoryAccess?.write ? { opacity: 0.5, cursor: "not-allowed" } : {}
          }
        >
          <MdAdd style={{ marginRight: 6 }} /> Add New Design
        </button>
      </div>

      <div className={styles.productTable}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Product name</th>
              <th style={{ textAlign: "center" }}>HSN Code</th>
              <th style={{ textAlign: "center" }}>Status</th>
              <th style={{ textAlign: "center" }}>Quantity</th>
              <th style={{ textAlign: "center" }}>Amount</th>
              <th style={{ textAlign: "center" }} className={styles.actionsHeader}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ textAlign: "center", padding: 30, color: "#aaa" }}
                >
                  Loading products...
                </td>
              </tr>
            ) : products.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ textAlign: "center", color: "#bbb", padding: "30px 0" }}
                >
                  No products found.
                </td>
              </tr>
            ) : (
              products.map((product) => (
                <tr
                  key={product.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleRowClick(product)}
                >
                  {/* Product name cell */}
                  <td style={{ verticalAlign: "middle" }}>
                    <div className={styles.productCell}>
                      <img
                        src={
                          product.images && product.images[0]?.file_name
                            ? mediaUrl(product.images[0].file_name)
                            : "/Assets/no-pictures.png"
                        }
                        alt={product.name}
                        className={styles.productImg}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "/Assets/no-pictures.png";
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(product);
                        }}
                        style={{ cursor: "pointer" }}
                      />
                      <div style={{ cursor: "pointer" }}>
                        <b
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(product);
                          }}
                          style={{
                            color: "#333",
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          {product.name}
                        </b>
                        <div>SKU: {product.sku_id}</div>
                      </div>
                    </div>
                  </td>

                  {/* NEW: HSN column */}
                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                    {displayHSN(product.hsn_code)}
                  </td>

                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                    {getStockLabel(product.quantity)}
                  </td>
                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                    Qty: {product.quantity}
                  </td>
                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                    <div>Basic Price: ₹{product.basic_door_price}</div>
                    <div className={styles.greenPrice}>
                      Double Door Price: ₹{product.double_door_price}
                    </div>
                  </td>
                  <td
                    style={{ textAlign: "center", verticalAlign: "middle" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className={styles.actions}>
                      {inventoryAccess?.update && (
                        <FaEdit
                          className={styles.editIcon}
                          onClick={() => {
                            setEditProduct(product);
                            setShowPanel(true);
                          }}
                          title="Edit"
                        />
                      )}
                      {inventoryAccess?.delete && (
                        <FaTrash
                          className={styles.deleteIcon}
                          onClick={() => deleteProduct(product.id)}
                          title="Delete"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Sentinel for infinite scroll */}
      <div ref={sentinelRef} style={{ height: 1 }} />

      {/* Bottom loaders / status */}
      {(loading && products.length === 0) && (
        <div style={{ textAlign: "center", padding: 20, color: "#aaa" }}>
          Loading products...
        </div>
      )}

      {(isFetchingNext && products.length > 0) && (
        <div style={{ textAlign: "center", padding: 16, color: "#888" }}>
          Loading more…
        </div>
      )}

      {(!loading && !isFetchingNext && products.length > 0 && !hasMore) && (
        <div style={{ textAlign: "center", padding: 16, color: "#999" }}>
          End of results
        </div>
      )}

      {/* --- ADD/EDIT PANEL: now handles DocUploadPopup logic in onSuccess --- */}
      {showPanel && (
        <AddProductPanel
          onClose={() => setShowPanel(false)}
          onSuccess={(createdProductId?: number) => {
            // Refresh from first page so the new product appears correctly at top according to sort
            setPage(1);
            setHasMore(true);
            setIsFetchingNext(false);
            (async () => {
              setLoading(true);
              const { list, pg } = await fetchPage(1);
              setProducts(list);
              setPagination(pg);
              const more = pg ? pg.current_page < pg.total_pages : list.length === DEFAULT_LIMIT;
              setHasMore(more);
              setLoading(false);
            })();

            setShowPanel(false);
            if (createdProductId) {
              setNewProductId(createdProductId);
              setShowDocPopup(true);
            }
          }}
          productToEdit={editProduct}
        />
      )}

      {/* --- DOC UPLOAD POPUP: only for new product add --- */}
      {showDocPopup && newProductId && (
        <DocUploadPopup
          open={showDocPopup}
          productId={newProductId}
          onClose={() => setShowDocPopup(false)}
        />
      )}
      </div>
    </div>
  );
};

export default AddProduct;
